/**
 * Secure Key Vault — WebAuthn-first, password-fallback encrypted key storage.
 *
 * Keys are encrypted at rest using AES-256-GCM in IndexedDB.
 * The encryption key is derived from either:
 *   1. A WebAuthn PRF extension (biometric/passkey) — preferred
 *   2. A master password via PBKDF2 — fallback
 *
 * When the vault is locked, IndexedDB only contains opaque encrypted blobs.
 * The AES CryptoKey is non-extractable and held in-memory only while unlocked.
 */

import type { KeyPair } from "./keys";

// ── Constants ────────────────────────────────────────────────────────────────

const DB_NAME = "signedplus_vault";
const DB_VERSION = 1;
const STORE_NAME = "vault";
const META_KEY = "vault_meta";
const DATA_KEY = "vault_data";
const PBKDF2_ITERATIONS = 100_000;
const AUTO_LOCK_MS = 30 * 60 * 1000; // 30 minutes

const OLD_STORAGE_KEY = "signedplus_pgp_keys";

// ── Types ────────────────────────────────────────────────────────────────────

export type VaultAuthMethod = "webauthn" | "password";

export type VaultStatus =
  | "uninitialized" // No vault exists yet
  | "locked" // Vault exists but is locked
  | "unlocked"; // Vault is open, keys are in memory

interface VaultMeta {
  version: number;
  authMethod: VaultAuthMethod;
  salt: string; // Base64-encoded PBKDF2 salt
  credentialId?: string; // Base64-encoded WebAuthn credential ID
  prfSalt?: string; // Base64-encoded PRF salt for WebAuthn
  createdAt: string;
}

interface VaultData {
  iv: string; // Base64-encoded AES-GCM IV
  ciphertext: string; // Base64-encoded encrypted key store
}

// ── Module State (in-memory only) ────────────────────────────────────────────

let _derivedKey: CryptoKey | null = null;
let _decryptedKeys: KeyPair[] = [];
let _autoLockTimer: ReturnType<typeof setTimeout> | null = null;
let _statusListeners: Set<(status: VaultStatus) => void> = new Set();
let _currentMeta: VaultMeta | null = null;

// ── Helpers: Base64 ↔ Uint8Array ─────────────────────────────────────────────

function toBase64(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  return btoa(String.fromCharCode.apply(null, Array.from(bytes)));
}

function fromBase64(b64: string): Uint8Array {
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}

// ── IndexedDB Helpers ────────────────────────────────────────────────────────

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function dbGet<T>(key: string): Promise<T | undefined> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

async function dbPut<T>(key: string, value: T): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    store.put(value, key);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
  });
}

// ── Auto-lock Timer ──────────────────────────────────────────────────────────

function resetAutoLockTimer() {
  if (_autoLockTimer) clearTimeout(_autoLockTimer);
  _autoLockTimer = setTimeout(() => {
    lockVault();
  }, AUTO_LOCK_MS);
}

function notifyListeners() {
  const status = getVaultStatusSync();
  _statusListeners.forEach((fn) => fn(status));
}

// ── Key Derivation ───────────────────────────────────────────────────────────

async function deriveKeyFromPassword(
  password: string,
  salt: Uint8Array,
): Promise<CryptoKey> {
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(password),
    "PBKDF2",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt.buffer as ArrayBuffer,
      iterations: PBKDF2_ITERATIONS,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false, // non-extractable!
    ["encrypt", "decrypt"],
  );
}

async function deriveKeyFromPRF(prfOutput: ArrayBuffer): Promise<CryptoKey> {
  // The PRF output is already high-entropy, but we use HKDF to derive AES key
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    prfOutput,
    "HKDF",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    {
      name: "HKDF",
      salt: new TextEncoder().encode("signedplus-vault-v1"),
      info: new TextEncoder().encode("aes-gcm-key"),
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

// ── Encryption / Decryption ──────────────────────────────────────────────────

async function encryptKeys(
  keys: KeyPair[],
  aesKey: CryptoKey,
): Promise<VaultData> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(JSON.stringify(keys));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv.buffer as ArrayBuffer },
    aesKey,
    plaintext,
  );
  return {
    iv: toBase64(iv),
    ciphertext: toBase64(ciphertext),
  };
}

async function decryptKeys(
  data: VaultData,
  aesKey: CryptoKey,
): Promise<KeyPair[]> {
  const iv = fromBase64(data.iv);
  const ciphertext = fromBase64(data.ciphertext);
  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: iv.buffer.slice(0) as ArrayBuffer },
    aesKey,
    ciphertext.buffer.slice(0) as ArrayBuffer,
  );
  return JSON.parse(new TextDecoder().decode(plaintext));
}

// ── WebAuthn Helpers ─────────────────────────────────────────────────────────

/**
 * Check if the browser supports WebAuthn with the PRF extension.
 * PRF (Pseudo-Random Function) allows us to derive a deterministic secret
 * from a biometric/passkey credential, which we use as our AES key source.
 */
export async function isWebAuthnAvailable(): Promise<boolean> {
  if (!window.PublicKeyCredential) return false;
  try {
    // Check for platform authenticator (Touch ID, Windows Hello, etc.)
    const available =
      await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    return available;
  } catch {
    return false;
  }
}

async function createWebAuthnCredential(prfSalt: Uint8Array): Promise<{
  credentialId: string;
  prfOutput: ArrayBuffer | null;
}> {
  const challenge = crypto.getRandomValues(new Uint8Array(32));

  const credential = (await navigator.credentials.create({
    publicKey: {
      challenge,
      rp: {
        name: "Signed+",
        id: window.location.hostname,
      },
      user: {
        id: crypto.getRandomValues(new Uint8Array(16)),
        name: "Signed+ Key Vault",
        displayName: "Signed+ Key Vault",
      },
      pubKeyCredParams: [
        { alg: -7, type: "public-key" }, // ES256
        { alg: -257, type: "public-key" }, // RS256
      ],
      authenticatorSelection: {
        authenticatorAttachment: "platform",
        userVerification: "required",
        residentKey: "preferred",
      },
      extensions: {
        prf: {
          eval: {
            first: prfSalt,
          },
        },
      } as any,
    },
  })) as PublicKeyCredential | null;

  if (!credential) throw new Error("WebAuthn credential creation cancelled");

  const response = credential.response as AuthenticatorAttestationResponse;
  const extResults = (credential as any).getClientExtensionResults?.();
  const prfResult = extResults?.prf?.results?.first;

  return {
    credentialId: toBase64(credential.rawId),
    prfOutput: prfResult || null,
  };
}

async function getWebAuthnPRF(
  credentialId: string,
  prfSalt: Uint8Array,
): Promise<ArrayBuffer> {
  const challenge = crypto.getRandomValues(new Uint8Array(32));

  const credential = (await navigator.credentials.get({
    publicKey: {
      challenge,
      allowCredentials: [
        {
          id: fromBase64(credentialId) as BufferSource,
          type: "public-key",
        },
      ],
      userVerification: "required",
      extensions: {
        prf: {
          eval: {
            first: prfSalt,
          },
        },
      } as any,
    },
  })) as PublicKeyCredential | null;

  if (!credential) throw new Error("WebAuthn authentication cancelled");

  const extResults = (credential as any).getClientExtensionResults?.();
  const prfResult = extResults?.prf?.results?.first;

  if (!prfResult) {
    throw new Error("PRF extension not supported by this authenticator");
  }

  return prfResult;
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Subscribe to vault status changes. Returns an unsubscribe function.
 */
export function onVaultStatusChange(
  listener: (status: VaultStatus) => void,
): () => void {
  _statusListeners.add(listener);
  return () => _statusListeners.delete(listener);
}

/**
 * Get the current vault status synchronously (from cached state).
 */
export function getVaultStatusSync(): VaultStatus {
  if (_derivedKey && _decryptedKeys) return "unlocked";
  if (_currentMeta) return "locked";
  return "uninitialized";
}

/**
 * Get the current vault status (checks IndexedDB if needed).
 */
export async function getVaultStatus(): Promise<VaultStatus> {
  if (_derivedKey) return "unlocked";

  const meta = await dbGet<VaultMeta>(META_KEY);
  _currentMeta = meta || null;
  return meta ? "locked" : "uninitialized";
}

/**
 * Get the auth method used by the vault.
 */
export async function getVaultAuthMethod(): Promise<VaultAuthMethod | null> {
  const meta = _currentMeta || (await dbGet<VaultMeta>(META_KEY));
  return meta?.authMethod || null;
}

/**
 * Check if there are legacy keys in localStorage that need migration.
 */
export function hasLegacyKeys(): boolean {
  const data = localStorage.getItem(OLD_STORAGE_KEY);
  if (!data) return false;
  try {
    const keys = JSON.parse(data);
    return Array.isArray(keys) && keys.length > 0;
  } catch {
    return false;
  }
}

/**
 * Get legacy keys from localStorage (for migration).
 */
function getLegacyKeys(): KeyPair[] {
  const data = localStorage.getItem(OLD_STORAGE_KEY);
  if (!data) return [];
  try {
    return JSON.parse(data);
  } catch {
    return [];
  }
}

/**
 * Initialize a new vault with WebAuthn (biometric).
 */
export async function initializeWithWebAuthn(): Promise<void> {
  const prfSalt = crypto.getRandomValues(new Uint8Array(32));
  const { credentialId, prfOutput } = await createWebAuthnCredential(prfSalt);

  if (!prfOutput) {
    throw new Error(
      "Your authenticator does not support the PRF extension. Please use a master password instead.",
    );
  }

  const aesKey = await deriveKeyFromPRF(prfOutput);
  const salt = crypto.getRandomValues(new Uint8Array(32)); // unused for webauthn but kept for schema consistency

  // Migrate legacy keys if they exist
  const initialKeys = hasLegacyKeys() ? getLegacyKeys() : [];

  const encryptedData = await encryptKeys(initialKeys, aesKey);

  const meta: VaultMeta = {
    version: 1,
    authMethod: "webauthn",
    salt: toBase64(salt),
    credentialId,
    prfSalt: toBase64(prfSalt),
    createdAt: new Date().toISOString(),
  };

  await dbPut(META_KEY, meta);
  await dbPut(DATA_KEY, encryptedData);

  // Clear legacy storage
  if (hasLegacyKeys()) {
    localStorage.removeItem(OLD_STORAGE_KEY);
  }

  _derivedKey = aesKey;
  _decryptedKeys = initialKeys;
  _currentMeta = meta;
  resetAutoLockTimer();
  notifyListeners();
}

/**
 * Initialize a new vault with a master password.
 */
export async function initializeWithPassword(password: string): Promise<void> {
  const salt = crypto.getRandomValues(new Uint8Array(32));
  const aesKey = await deriveKeyFromPassword(password, salt);

  // Migrate legacy keys if they exist
  const initialKeys = hasLegacyKeys() ? getLegacyKeys() : [];

  const encryptedData = await encryptKeys(initialKeys, aesKey);

  const meta: VaultMeta = {
    version: 1,
    authMethod: "password",
    salt: toBase64(salt),
    createdAt: new Date().toISOString(),
  };

  await dbPut(META_KEY, meta);
  await dbPut(DATA_KEY, encryptedData);

  // Clear legacy storage
  if (hasLegacyKeys()) {
    localStorage.removeItem(OLD_STORAGE_KEY);
  }

  _derivedKey = aesKey;
  _decryptedKeys = initialKeys;
  _currentMeta = meta;
  resetAutoLockTimer();
  notifyListeners();
}

/**
 * Unlock the vault with WebAuthn (biometric).
 */
export async function unlockWithWebAuthn(): Promise<void> {
  const meta = await dbGet<VaultMeta>(META_KEY);
  if (!meta) throw new Error("No vault found");
  if (meta.authMethod !== "webauthn")
    throw new Error("Vault uses password authentication");
  if (!meta.credentialId || !meta.prfSalt)
    throw new Error("Missing WebAuthn credentials");

  const prfSalt = fromBase64(meta.prfSalt);
  const prfOutput = await getWebAuthnPRF(meta.credentialId, prfSalt);
  const aesKey = await deriveKeyFromPRF(prfOutput);

  const data = await dbGet<VaultData>(DATA_KEY);
  if (!data) throw new Error("Vault data not found");

  // This will throw if the key is wrong (AES-GCM authentication tag check)
  _decryptedKeys = await decryptKeys(data, aesKey);
  _derivedKey = aesKey;
  _currentMeta = meta;
  resetAutoLockTimer();
  notifyListeners();
}

/**
 * Unlock the vault with a master password.
 */
export async function unlockWithPassword(password: string): Promise<void> {
  const meta = await dbGet<VaultMeta>(META_KEY);
  if (!meta) throw new Error("No vault found");
  if (meta.authMethod !== "password")
    throw new Error("Vault uses WebAuthn authentication");

  const salt = fromBase64(meta.salt);
  const aesKey = await deriveKeyFromPassword(password, salt);

  const data = await dbGet<VaultData>(DATA_KEY);
  if (!data) throw new Error("Vault data not found");

  try {
    _decryptedKeys = await decryptKeys(data, aesKey);
  } catch {
    throw new Error("Incorrect password");
  }

  _derivedKey = aesKey;
  _currentMeta = meta;
  resetAutoLockTimer();
  notifyListeners();
}

/**
 * Lock the vault — clears all in-memory key material.
 */
export function lockVault(): void {
  _derivedKey = null;
  _decryptedKeys = [];
  if (_autoLockTimer) {
    clearTimeout(_autoLockTimer);
    _autoLockTimer = null;
  }
  notifyListeners();
}

/**
 * Get all keys from the unlocked vault.
 * Returns empty array if vault is locked.
 */
export function getVaultKeys(): KeyPair[] {
  resetAutoLockTimer();
  return [..._decryptedKeys];
}

/**
 * Save a key to the vault (add or update by ID).
 */
export async function saveVaultKey(key: KeyPair): Promise<void> {
  if (!_derivedKey) throw new Error("Vault is locked");

  const filtered = _decryptedKeys.filter((k) => k.id !== key.id);
  filtered.push(key);
  _decryptedKeys = filtered;

  const encryptedData = await encryptKeys(_decryptedKeys, _derivedKey);
  await dbPut(DATA_KEY, encryptedData);
  resetAutoLockTimer();
  notifyListeners();
}

/**
 * Delete a key from the vault by ID.
 */
export async function deleteVaultKey(id: string): Promise<void> {
  if (!_derivedKey) throw new Error("Vault is locked");

  _decryptedKeys = _decryptedKeys.filter((k) => k.id !== id);

  const encryptedData = await encryptKeys(_decryptedKeys, _derivedKey);
  await dbPut(DATA_KEY, encryptedData);
  resetAutoLockTimer();
  notifyListeners();
}

/**
 * Destroy the vault completely. This is irreversible.
 */
export async function destroyVault(): Promise<void> {
  lockVault();
  _currentMeta = null;
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    store.clear();
    tx.oncomplete = () => {
      db.close();
      notifyListeners();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
  });
}
